// Types pour le calendrier des chantiers et affectations

// Structure d'un jour/demi-journée dans jours_details
export interface JourDetail {
  date: string; // Format YYYY-MM-DD
  period: 'AM' | 'PM' | 'FULL';
  value: number; // 0.5 pour demi-journée, 1 pour journée complète
}

export interface CalendarAffectation {
  id: string;
  chantier_id: string;
  membre_equipe_id: string;
  equipe_id: string | null;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number | null;
  jours_travailles: number;
  taux_horaire_specifique: number | null;
  charges_salariales_pct: number | null;
  charges_patronales_pct: number | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Heure de RDV sur le chantier (format HH:MM, défaut: '07:30')
  heure_debut?: string | null;
  // Détail précis des jours/demi-journées sélectionnés (pour les retards)
  jours_details?: JourDetail[] | null;
  // Relations jointes
  chantier?: {
    id: string;
    nom_chantier: string;
    client: string;
    adresse: string | null;
    etat_chantier: string | null;
    pictogramme?: string;
    couleur?: string;
    // Téléphone du client pour contact terrain
    telephone_client?: string | null;
  };
  membre?: {
    id: string;
    nom: string;
    prenom: string;
    taux_horaire: number;
    poste: string | null;
    specialite: string | null;
    charges_salariales: number;
    charges_patronales: number;
    charges_salariales_pct: number | null;
    charges_patronales_pct: number | null;
    coefficient_charges?: number;
    heures_par_jour?: number;
  };
  equipe?: {
    id: string;
    nom: string;
    specialite: string | null;
    pictogramme?: string;
    couleur?: string;
  };
}

export interface CalendarDay {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  affectations: CalendarAffectation[];
  // Pour les points colorés sous le jour
  specialites: string[];
}

export interface DayPlanning {
  date: Date;
  affectations: CalendarAffectation[];
  totalHeures: number;
  totalCout: number;
  membresCount: number;
  chantiersCount: number;
}

export interface AffectationFormData {
  chantier_id: string;
  type_affectation: 'membre' | 'equipe';
  membre_equipe_id?: string;
  equipe_id?: string;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number;
  jours_travailles: number;
  notes: string;
}

// Couleurs par spécialité d'équipe/membre
export const SPECIALITE_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  'Gros œuvre': { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', dot: 'bg-orange-500' },
  'Maçonnerie': { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', dot: 'bg-orange-500' },
  'Carrelage': { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', dot: 'bg-blue-500' },
  'Plomberie': { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-400', dot: 'bg-cyan-500' },
  'Électricité': { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-400', dot: 'bg-yellow-500' },
  'Peinture': { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400', dot: 'bg-purple-500' },
  'Menuiserie': { bg: 'bg-amber-500/20', border: 'border-amber-500', text: 'text-amber-400', dot: 'bg-amber-500' },
  'Couverture': { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500' },
  'Isolation': { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', dot: 'bg-green-500' },
  'Chauffage': { bg: 'bg-rose-500/20', border: 'border-rose-500', text: 'text-rose-400', dot: 'bg-rose-500' },
  'default': { bg: 'bg-gray-500/20', border: 'border-gray-500', text: 'text-gray-400', dot: 'bg-gray-500' }
};

export function getSpecialiteColor(specialite: string | null | undefined) {
  if (!specialite) return SPECIALITE_COLORS['default'];
  return SPECIALITE_COLORS[specialite] || SPECIALITE_COLORS['default'];
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES POUR LES SUSPENSIONS DE CHANTIERS
// ═══════════════════════════════════════════════════════════════════════════

// Raisons possibles de suspension
export type SuspensionRaison = 'intemperies' | 'attente_materiaux' | 'probleme_client' | 'autre';

// Durées estimées de suspension
export type SuspensionDuree = '1_semaine' | '2_semaines' | '1_mois' | 'personnalisee' | 'indeterminee';

// Types de suspension
export type SuspensionType = 'totale' | 'partielle';

// Types de reprise
export type RepriseType = 'equipe_origine' | 'nouvelle_equipe' | 'partielle';

// Libellés des raisons de suspension
export const SUSPENSION_RAISONS: Record<SuspensionRaison, string> = {
  intemperies: 'Intempéries',
  attente_materiaux: 'Attente matériaux',
  probleme_client: 'Problème client',
  autre: 'Autre',
};

// Libellés des durées estimées
export const SUSPENSION_DUREES: Record<SuspensionDuree, string> = {
  '1_semaine': '1 semaine',
  '2_semaines': '2 semaines',
  '1_mois': '1 mois',
  'personnalisee': 'Personnalisée',
  'indeterminee': 'Indéterminée',
};

// Snapshot d'une affectation suspendue (stocké en JSONB)
export interface AffectationSuspendue {
  membre_equipe_id: string;
  membre_nom: string;
  membre_prenom: string;
  equipe_id: string | null;
  date_debut_originale: string;
  date_fin_originale: string;
  heures_par_jour: number;
  taux_horaire: number;
  jours_details?: JourDetail[] | null;
  // Champs pour distinguer le type d'affectation (rétrocompatible, optionnel)
  notes?: string | null;
  type_affectation?: 'normale' | 'retard' | 'reprise';
}

// Information complète d'une suspension
export interface SuspensionInfo {
  id: string;
  chantier_id: string;

  // Informations de suspension
  date_suspension: string;
  derniere_demi_journee: 'journee_complete' | 'matin' | 'apres_midi';
  raison: SuspensionRaison;
  raison_detail: string | null;

  // Durée estimée
  duree_estimee: SuspensionDuree | null;
  date_reprise_estimee: string | null;

  // Type de suspension
  type_suspension: SuspensionType;

  // Snapshot des affectations
  affectations_suspendues: AffectationSuspendue[];

  // Coût de remobilisation
  cout_mobilisation: number;

  // Informations de reprise (null si toujours suspendu)
  date_reprise: string | null;
  duree_suspension_jours: number | null;
  type_reprise: RepriseType | null;

  // Metadata
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

// Props pour les modals de suspension/reprise
export interface SuspensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  chantierId: string;
  chantierNom: string;
  onConfirm: () => void;
}

export interface RepriseModalProps extends SuspensionModalProps {
  suspensionInfo: SuspensionInfo;
}

// Données du formulaire de suspension (étapes du wizard)
export interface SuspensionFormData {
  // Étape 2: Type
  type_suspension: SuspensionType;
  membres_suspendus: string[]; // IDs des membres à suspendre (si partielle)

  // Étape 3: Détails
  date_suspension: string;
  derniere_demi_journee: 'journee_complete' | 'matin' | 'apres_midi';
  raison: SuspensionRaison;
  raison_detail: string;
  duree_estimee: SuspensionDuree;
  date_reprise_estimee?: string; // Date manuelle si duree_estimee === 'personnalisee'
}

// Données du formulaire de reprise (étapes du wizard)
export interface RepriseFormData {
  // Étape 1: Choix équipe
  type_reprise: 'equipe_origine' | 'nouvelle_equipe';

  // Étape 2: Configuration
  date_reprise: string;
  date_fin: string;
  heures_par_jour: number;
  jours_travailles: number; // Nombre de jours calendaires (inclusif)

  // Si nouvelle équipe
  membres_selectionnes?: string[]; // IDs des membres
  equipes_selectionnees?: string[]; // IDs des équipes
}
